import type { TaskResult } from "../types";
import {
  ANSWER_CELLS,
  CATALOGO,
  CONSULTAS,
  LAYOUT,
  PEDIDOS,
  PRECIOS_REGION,
  REGIONES,
  SHEETS,
  TRAMOS_COMISION,
  VENTAS_VENDEDORES,
  excelDate,
} from "../data/funciones/buscarx.data";
import {
  type CheckResult,
  checkBlock,
  checkCell,
  checkSpill,
  countFunction,
} from "./functionChecks";

const FN = "XLOOKUP";

const productoDe = (sku: string) => CATALOGO.find((row) => row[0] === sku);

function comisionPara(ventas: number): number {
  let pct = TRAMOS_COMISION[0][1];
  for (const [desde, tramo] of TRAMOS_COMISION) {
    if (ventas >= desde) pct = tramo;
  }
  return pct;
}

async function grade(
  taskId: number,
  check: (context: Excel.RequestContext) => Promise<CheckResult>
): Promise<TaskResult> {
  try {
    return await Excel.run(async (context) => ({ taskId, ...(await check(context)) }));
  } catch {
    return { taskId, passed: false };
  }
}

export async function gradeFuncionesBuscarx(): Promise<TaskResult[]> {
  const cell = (taskId: number) => ANSWER_CELLS[taskId];

  return [
    await grade(1, (ctx) =>
      checkCell(ctx, cell(1).sheet, cell(1).address, FN, productoDe(CONSULTAS.skuPrecio)?.[4])
    ),

    await grade(2, (ctx) =>
      checkBlock(
        ctx,
        SHEETS.pedidos,
        `F4:F${LAYOUT.pedidosLastRow}`,
        FN,
        PEDIDOS.map(([, , sku]) => [productoDe(sku)?.[1]])
      )
    ),

    await grade(3, (ctx) =>
      checkCell(ctx, cell(3).sheet, cell(3).address, FN, "No encontrado")
    ),

    await grade(4, (ctx) =>
      checkCell(
        ctx,
        cell(4).sheet,
        cell(4).address,
        FN,
        CATALOGO.find((row) => row[1] === CONSULTAS.productoParaSku)?.[0]
      )
    ),

    await grade(5, (ctx) => {
      const row = productoDe(CONSULTAS.skuDetalle);
      return checkSpill(ctx, cell(5).sheet, cell(5).address, FN, [
        [row?.[1], row?.[2], row?.[3]],
      ]);
    }),

    await grade(6, (ctx) => {
      const first = LAYOUT.vendedoresFirstRow;
      const last = first + VENTAS_VENDEDORES.length - 1;
      return checkBlock(
        ctx,
        SHEETS.consultas,
        `C${first}:C${last}`,
        FN,
        VENTAS_VENDEDORES.map(([, ventas]) => [comisionPara(ventas)])
      );
    }),

    await grade(7, (ctx) => {
      const texto = CONSULTAS.textoParcial.toLowerCase();
      const first = CATALOGO.find((row) => row[1].toLowerCase().includes(texto));
      return checkCell(ctx, cell(7).sheet, cell(7).address, FN, first?.[1]);
    }),

    await grade(8, (ctx) => {
      const last = [...PEDIDOS].reverse().find((row) => row[2] === CONSULTAS.skuUltimoPedido);
      return checkCell(
        ctx,
        cell(8).sheet,
        cell(8).address,
        FN,
        last ? excelDate(last[1]) : undefined
      );
    }),

    await grade(9, async (ctx) => {
      const regionIndex = REGIONES.indexOf(CONSULTAS.region);
      const row = PRECIOS_REGION.find((item) => item[0] === CONSULTAS.productoRegion);
      const result = await checkCell(
        ctx,
        cell(9).sheet,
        cell(9).address,
        FN,
        row?.[1 + regionIndex]
      );
      if (!result.passed) return result;

      const range = ctx.workbook.worksheets
        .getItem(cell(9).sheet)
        .getRange(cell(9).address);
      range.load("formulas");
      await ctx.sync();
      return countFunction(range.formulas[0][0], FN) >= 2
        ? result
        : {
            passed: false,
            message: "Usa dos BUSCARX, uno dentro del otro: uno para la región y otro para el producto.",
          };
    }),
  ];
}
